import { c as _c } from "react/compiler-runtime";
import React, { FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchableSelect, SearchableSelectProps } from 'record-it-components/input/SearchableSelect/SearchableSelect';
import { prepareProps } from 'record-it-components/utils/propUtils';
import { PropsWithoutChildren } from 'record-it-components/utils/utilityTypes';
import { UserGroup } from '../../../classes/entities/UserGroup/userGroupTypes';
import { UserGroupUtils } from '../../../classes/entities/UserGroup/UserGroupUtils';
import { useNullableSelectEntityItems } from '../../../hooks/entity/useNullableSelectEntityItems/useNullableSelectEntityItems';
export type UserGroupSelectProps = PropsWithoutChildren<Omit<SearchableSelectProps<UserGroup>, 'getKeyOfItem' | 'getDisplayTextOfItem' | 'items' | 'value' | 'onValueChanged' | 'label'>> & {
  /**
   * ID of the UserGroup selected by default.
   */
  readonly userGroupId: string | null;
  /**
   * Called whenever the user selects a new UserGroup.
   */
  readonly onUserGroupChanged: (value: string | null) => void;
  /**
   * UserGroups the user can select from.
   *
   * If you include `null`, the user will be able to select "no usergroup".
   */
  readonly availableUserGroups: Array<UserGroup | null>;
  /**
   * Label to show above the select component.
   *
   * @default "Benutzergruppe"
   */
  readonly label?: string;
};
export const UserGroupSelect: FunctionComponent<UserGroupSelectProps> = React.memo(props => {
  const $ = _c(29);
  let t0;
  if ($[0] !== props) {
    t0 = prepareProps({
      props,
      ignoreChildren: true
    });
    $[0] = props;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  let availableUserGroups;
  let label;
  let onUserGroupChanged;
  let remainingProps;
  let userGroupId;
  if ($[2] !== t0) {
    ({
      userGroupId,
      availableUserGroups,
      label,
      onUserGroupChanged,
      ...remainingProps
    } = t0);
    $[2] = t0;
    $[3] = availableUserGroups;
    $[4] = label;
    $[5] = onUserGroupChanged;
    $[6] = remainingProps;
    $[7] = userGroupId;
  } else {
    availableUserGroups = $[3];
    label = $[4];
    onUserGroupChanged = $[5];
    remainingProps = $[6];
    userGroupId = $[7];
  }
  const {
    t
  } = useTranslation();
  let t1;
  if ($[8] !== t) {
    t1 = t("components.input.UserGroupSelect.userGroupNotFound");
    $[8] = t;
    $[9] = t1;
  } else {
    t1 = $[9];
  }
  let t2;
  if ($[10] !== t) {
    t2 = t("components.input.UserGroupSelect.noUserGroup");
    $[10] = t;
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== availableUserGroups || $[13] !== onUserGroupChanged || $[14] !== t1 || $[15] !== t2 || $[16] !== userGroupId) {
    t3 = {
      value: userGroupId,
      entities: availableUserGroups,
      entityNotFoundText: t1,
      nullValueText: t2,
      onValueChanged: onUserGroupChanged,
      getDisplayTextOfEntity: UserGroupUtils.displayTextGetter,
      getKeyOfEntity: UserGroupUtils.keyGetter,
      getValueOfEntity: _temp
    };
    $[12] = availableUserGroups;
    $[13] = onUserGroupChanged;
    $[14] = t1;
    $[15] = t2;
    $[16] = userGroupId;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  const useSelectEntityItemsReturnValue = useNullableSelectEntityItems(t3);
  let t4;
  if ($[18] !== label || $[19] !== t) {
    t4 = label != null ? label : t("components.input.UserGroupSelect.label");
    $[18] = label;
    $[19] = t;
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  let t5;
  if ($[21] !== remainingProps || $[22] !== t4 || $[23] !== useSelectEntityItemsReturnValue.getDisplayTextOfItem || $[24] !== useSelectEntityItemsReturnValue.getKeyOfItem || $[25] !== useSelectEntityItemsReturnValue.items || $[26] !== useSelectEntityItemsReturnValue.onSelectedItemChanged || $[27] !== useSelectEntityItemsReturnValue.selectedItem) {
    t5 = <SearchableSelect label={t4} value={useSelectEntityItemsReturnValue.selectedItem} items={useSelectEntityItemsReturnValue.items} onValueChanged={useSelectEntityItemsReturnValue.onSelectedItemChanged} getDisplayTextOfItem={useSelectEntityItemsReturnValue.getDisplayTextOfItem} getKeyOfItem={useSelectEntityItemsReturnValue.getKeyOfItem} data-testid="UserGroupSelect.SearchableSelect" {...remainingProps} />;
    $[21] = remainingProps;
    $[22] = t4;
    $[23] = useSelectEntityItemsReturnValue.getDisplayTextOfItem;
    $[24] = useSelectEntityItemsReturnValue.getKeyOfItem;
    $[25] = useSelectEntityItemsReturnValue.items;
    $[26] = useSelectEntityItemsReturnValue.onSelectedItemChanged;
    $[27] = useSelectEntityItemsReturnValue.selectedItem;
    $[28] = t5;
  } else {
    t5 = $[28];
  }
  return t5;
});
function _temp(group) {
  return group.id;
}